// src/pages/Cart.jsx
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export default function Cart() {
  const { cart, loading, updateCartItem, removeFromCart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(null);

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdating(productId);
    await updateCartItem(productId, newQuantity);
    setUpdating(null);
  };

  const handleRemove = async (productId) => {
    setUpdating(productId);
    await removeFromCart(productId);
    setUpdating(null);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">Loading cart...</div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Cart is Empty</h2>
        <p className="text-gray-600 mb-8">Add some handmade items to get started!</p>
        <button
          onClick={() => navigate("/products")}
          className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition"
        >
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((item) => (
            <div
              key={item.product._id}
              className="bg-white rounded-lg shadow-md p-4 flex gap-4"
            >
              {/* Product Image */}
              <img
                src={item.productSnapshot.image || "/placeholder.png"}
                alt={item.productSnapshot.name}
                className="w-24 h-24 object-cover rounded"
              />

              {/* Product Details */}
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900">
                  {item.productSnapshot.name}
                </h3>
                <p className="text-purple-600 font-bold mt-1">₹{item.price}</p>

                {/* Stock Info */}
                {item.product.stock < 5 && (
                  <p className="text-red-500 text-sm mt-1">
                    Only {item.product.stock} left in stock!
                  </p>
                )}
              </div>

              {/* Quantity Controls */}
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2 border rounded">
                  <button
                    onClick={() =>
                      handleQuantityChange(item.product._id, item.quantity - 1)
                    }
                    disabled={updating === item.product._id || item.quantity <= 1}
                    className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                  >
                    −
                  </button>
                  <span className="px-3 py-1 font-medium">{item.quantity}</span>
                  <button
                    onClick={() =>
                      handleQuantityChange(item.product._id, item.quantity + 1)
                    }
                    disabled={
                      updating === item.product._id ||
                      item.quantity >= item.product.stock
                    }
                    className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={() => handleRemove(item.product._id)}
                  disabled={updating === item.product._id}
                  className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                >
                  Remove
                </button>

                <p className="text-gray-700 font-semibold mt-2">
                  ₹{item.price * item.quantity}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Order Summary
            </h2>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal ({cart.totalItems} items)</span>
                <span>₹{getCartTotal()}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Shipping</span>
                <span className="text-green-600">FREE</span>
              </div>
              <div className="border-t pt-3 flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-purple-600">₹{getCartTotal()}</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition font-semibold"
            >
              Proceed to Checkout
            </button>

            <button
              onClick={() => navigate("/products")}
              className="w-full mt-3 border border-purple-600 text-purple-600 py-3 rounded-lg hover:bg-purple-50 transition font-semibold"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}